import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import {
  useListPlatformTenantsQuery,
  PlatformTenant,
} from '@/redux/internal-services/platformAdminApi';

const STATUS_BADGE: Record<string, string> = {
  Active:      'bg-green-100 text-green-800',
  Trial:       'bg-blue-100 text-blue-800',
  Suspended:   'bg-yellow-100 text-yellow-800',
  Deactivated: 'bg-red-100 text-red-800',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-700'}`}>
    {status}
  </span>
);

const STATUSES = ['All', 'Active', 'Trial', 'Suspended', 'Deactivated'];

const TenantsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading, isFetching } = useListPlatformTenantsQuery(
    { search: search || undefined, status: status || undefined },
    { refetchOnMountOrArgChange: true }
  );

  const tenants: PlatformTenant[] = data?.data ?? [];
  const total = data?.total ?? 0;

  const activeCount   = tenants.filter((t) => t.lifecycle_status === 'Active').length;
  const trialCount    = tenants.filter((t) => t.lifecycle_status === 'Trial').length;
  const suspCount     = tenants.filter((t) => t.lifecycle_status === 'Suspended').length;

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-ui-text">Tenant Management</h1>
            <p className="text-sm text-ui-text2 mt-1">View and manage all tenants across the platform.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-text3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7 7 0 104.65 4.65a7 7 0 0012 12z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants..."
                className="w-full pl-9 pr-4 py-2 border border-ui-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s === 'All' ? '' : s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    (s === 'All' ? status === '' : status === s)
                      ? 'bg-brand-dark text-white'
                      : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Tenants', value: total, color: 'text-brand-dark' },
          { label: 'Active', value: activeCount, color: 'text-green-700' },
          { label: 'Trial', value: trialCount, color: 'text-blue-700' },
          { label: 'Suspended', value: suspCount, color: 'text-yellow-700' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-ui-card border border-ui-border2 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium text-ui-text2 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{isLoading ? '—' : value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-ui-card border border-ui-border2 rounded-xl shadow-sm overflow-hidden">
        {isLoading || isFetching ? (
          <div className="py-20 text-center text-ui-text3 text-sm">Loading tenants...</div>
        ) : tenants.length === 0 ? (
          <div className="py-20 text-center">
            <div className="text-ui-text3 text-5xl mb-4">🏢</div>
            <p className="text-ui-text3 text-sm">{t('platform.noTenantsFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ui-border bg-ui-raised">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden md:table-cell">Plan</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden lg:table-cell">Users</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden lg:table-cell">Created</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-border">
                {tenants.map((tenant) => (
                  <tr
                    key={tenant.tenant_id}
                    className="hover:bg-ui-hover transition cursor-pointer"
                    onClick={() => navigate(`/platform/tenants/${tenant.tenant_id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-ui-text">{tenant.company_name}</p>
                        {tenant.primary_contact?.email && (
                          <p className="text-xs text-ui-text3 mt-0.5">{tenant.primary_contact.email}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={tenant.lifecycle_status} />
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="text-sm text-ui-text2">{tenant.plan_name ?? '—'}</span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-sm text-ui-text2">{tenant.user_count ?? '—'}</span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-sm text-ui-text3">
                        {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/platform/tenants/${tenant.tenant_id}`); }}
                        className="text-xs text-brand-dark font-medium hover:underline"
                      >
                        Manage →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default TenantsPage;
